import { BE1_API } from '../config/api';
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

// Airflow BE1 API에서 connection 목록을 가져오는 thunk
export const fetchConnections = createAsyncThunk<string[], void, { rejectValue: string }>(
  'connections/fetchConnections',
  async (_, { rejectWithValue }) => {
    try {
      const res = await fetch(`${BE1_API}/api/connections/`);
      if (!res.ok) throw new Error(await res.text());
      const list = await res.json(); // ex: ["conn1", "conn2", ...]
      // 「내 PC 에만 저장」 연결 — 서버가 값을 아직 갖지 않았으면 이 PC 금고의 값을 보낸다(조회·미리보기용, services/pcConnections).
      // 동적 import — services/pcConnections 가 store 를 부르므로 정적으로 잇으면 순환한다
      if (Array.isArray(list) && list.some((c: any) => c && c.storage === 'local')) {
        import('../services/pcConnections').then((m) => m.warmPcConns(list)).catch(() => undefined);
      }
      return list;
    } catch (e) {
      return rejectWithValue((e as Error).message);
    }
  }
);

// Airflow BE1 API에서 특정 connection의 테이블 목록을 가져오는 thunk
/** 백엔드 GET /api/connections/{id}/tables/ 의 항목 — main.py 가 {"name": row[0], "comment": row[1]} 로 조립.
 *  응답 전체는 {tables: TableInfo[]} 래퍼. (CustomTableSelectWidget 의 모달도 table.name 을 쓴다) */
export type TableInfo = { name: string; comment?: string | null };

/** tableMap 항목(문자열/객체 혼재 가능)에서 테이블명만 뽑는다 — 소비처 공용 헬퍼 */
export const tableName = (t: TableInfo | string): string =>
  typeof t === 'string' ? t : (t.name ?? (t as any).table_name ?? '');

/** DB 커넥션은 '접속 계정' 이라 여러 스키마가 보인다 — 스키마를 고른 뒤 그 안의 테이블을 고른다.
 *  tableMap 의 키는 `${connection_id}::${schema}` (스키마 미지정이면 커넥션 기본 스키마 = 빈 문자열). */
export const tableMapKey = (connection_id: string, schema?: string) => `${connection_id}::${schema || ''}`;

export const fetchSchemas = createAsyncThunk<
  { connection_id: string; schemas: string[]; def: string },
  string,
  { rejectValue: string }
>(
  'connections/fetchSchemas',
  async (connection_id, { rejectWithValue }) => {
    try {
      const res = await fetch(`${BE1_API}/api/connections/${encodeURIComponent(connection_id)}/schemas/`);
      if (!res.ok) throw new Error(await res.text());
      const raw = await res.json();
      return { connection_id, schemas: raw?.schemas || [], def: raw?.default || '' };
    } catch (e) {
      return rejectWithValue((e as Error).message);
    }
  }
);

export const fetchTables = createAsyncThunk<
  { connection_id: string; schema: string; tables: TableInfo[] },
  string | { connection_id: string; schema?: string },
  { rejectValue: string }
>(
  'connections/fetchTables',
  async (arg, { rejectWithValue }) => {
    const connection_id = typeof arg === 'string' ? arg : arg.connection_id;
    const schema = (typeof arg === 'string' ? '' : arg.schema) || '';
    try {
      const qs = schema ? `?schema=${encodeURIComponent(schema)}` : '';
      const res = await fetch(`${BE1_API}/api/connections/${encodeURIComponent(connection_id)}/tables/${qs}`);
      if (!res.ok) throw new Error(await res.text());
      const raw = await res.json(); // {tables: [{table_name, comment}]} 또는 (구형) 배열
      const arr: any[] = Array.isArray(raw) ? raw : (raw && Array.isArray(raw.tables) ? raw.tables : []);
      // 항상 TableInfo[] 로 정규화 — 래퍼 객체를 그대로 저장하면 소비처의 .map/.includes 가 깨진다
      const tables: TableInfo[] = arr.map((t) => (typeof t === 'string' ? { name: t } : t));
      return { connection_id, schema, tables };
    } catch (e) {
      return rejectWithValue((e as Error).message);
    }
  }
);

/** 백엔드 GET /api/connections/ 의 항목 (Airflow Connection). 예전엔 string[] 로 선언돼 있어
 *  객체가 들어와도 tsc 가 잡지 못했고, <option>{c}</option> 처럼 렌더하면 런타임 오류가 났다. */
export type ConnectionInfo = {
  connection_id: string;
  conn_type?: string;
  description?: string | null;
  host?: string | null;
  login?: string | null;
  port?: number | null;
  schema?: string | null;
  /** 웹 — server: 서버(Airflow)에 저장 · local: 내 PC 에만(값은 이 브라우저 금고, services/localVault) */
  storage?: 'server' | 'local';
  /** local — 서버가 지금 값을 메모리에 갖고 있는가 */
  unlocked?: boolean;
  /** 무설치판 — 다른 PC·다른 키로 봉인되어 풀 수 없다(다시 입력) */
  unreadable?: boolean;
};

type ConnectionsState = {
  list: ConnectionInfo[];
  loading: boolean;
  error: string | null;
  tableMap: Record<string, TableInfo[]>;      // `${connection_id}::${schema}` 별 테이블 목록
  schemaMap: Record<string, string[]>;        // connection_id 별 스키마 목록
  /** tableMap 키별로 목록을 DB 에서 읽은 시각(ISO) — 화면이 '언제 읽은 목록인지' 보여 주고 갱신을 권한다 */
  tableFetchedAt: Record<string, string>;
  defaultSchema: Record<string, string>;      // connection_id 별 기본 스키마
};

const initialState: ConnectionsState = { list: [], loading: false, error: null, tableMap: {}, schemaMap: {}, defaultSchema: {}, tableFetchedAt: {} };

const connectionsSlice = createSlice({
  name: 'connections',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchConnections.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConnections.fulfilled, (state, action: PayloadAction<Array<ConnectionInfo | string>>) => {
        state.loading = false;
        // 문자열/객체 어느 쪽이 와도 ConnectionInfo 로 정규화하고 connection_id 기준으로 중복 제거
        const byId = new Map<string, ConnectionInfo>();
        byId.set('test_conn', { connection_id: 'test_conn' });
        for (const item of action.payload || []) {
          const info: ConnectionInfo = typeof item === 'string' ? { connection_id: item } : item;
          if (info && info.connection_id) byId.set(info.connection_id, info);
        }
        state.list = Array.from(byId.values());
      })
      .addCase(fetchConnections.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string | null;
      })
      .addCase(fetchTables.fulfilled, (state, action) => {
        const { connection_id, schema, tables } = action.payload;
        state.tableMap[tableMapKey(connection_id, schema)] = tables;
        state.tableFetchedAt[tableMapKey(connection_id, schema)] = new Date().toISOString();
        if (!schema) state.tableMap[connection_id] = tables;   // 스키마 미지정 호출의 예전 키도 채워 둔다
      })
      .addCase(fetchSchemas.fulfilled, (state, action) => {
        const { connection_id, schemas, def } = action.payload;
        state.schemaMap[connection_id] = schemas;
        state.defaultSchema[connection_id] = def;
      });
  },
});

export default connectionsSlice.reducer; 